import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Reveal } from '../components/motion';
import SEO from '../components/SEO';
import { SectionTag, ArrowBtn, PageHero, ChainFlow } from '../components/bits';
import { useLead } from '../components/LeadModal';
import { useContent } from '../lib/content';
import { LAYERS, TRIAD, JOURNEY, ROADMAP, IMAGES } from '../content/data';

function LayersExplorer() {
  const [active, setActive] = useState(0);
  const { ctList } = useContent();
  const layers = ctList('about.layers', LAYERS);
  const current = layers[Math.min(active, layers.length - 1)];
  return (
    <div data-testid="layers-explorer" className="grid gap-6 lg:grid-cols-[1fr_1.1fr]">
    <SEO title="About DITEC" description="An ecosystem built around digital capability — connecting talent, enterprise demand, research and capital." />
      <div className="space-y-2">
        {layers.map((l, i) => (
          <button
            key={l.n}
            data-testid={`layer-tab-${l.n}`}
            onClick={() => setActive(i)}
            className={`flex w-full touch-target items-center gap-4 rounded-2xl border px-5 py-4 text-left transition-colors duration-300 ${
              active === i ? 'border-gold/50 bg-navy-800 text-white' : 'border-white/10 bg-transparent text-white/60 hover:border-white/25 hover:text-white'
            }`}
          >
            <span className={`font-mono text-xs ${active === i ? 'text-gold' : 'text-white/35'}`}>{l.n}</span>
            <span className="text-sm font-semibold tracking-tight sm:text-base">{l.name}</span>
          </button>
        ))}
      </div>
      <div className="relative min-h-[220px] overflow-hidden rounded-3xl border border-gold/20 bg-navy-800/70 p-8 sm:p-10">
        <AnimatePresence mode="wait">
          <motion.div key={active} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -18 }} transition={{ duration: 0.35 }}>
            <span className="font-mono text-5xl font-semibold text-gold/25">{current.n}</span>
            <h3 data-testid="layer-detail-title" className="text-h3 mt-4 font-semibold text-white">{current.name}</h3>
            <p data-testid="layer-detail-desc" className="mt-4 max-w-md text-base leading-relaxed text-white/70">{current.desc}</p>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

export default function About() {
  const { open } = useLead();
  const { ctList } = useContent();
  return (
    <div data-testid="about-page">
      <PageHero
        ck="about"
        dark
        tag="About DITEC"
        lines={<>An Ecosystem Built Around <span className="font-serif italic font-normal text-gold">Digital Capability</span></>}
        intro="DITEC — the Digital Intelligence & Technology Excellence Council — is designed to connect digital talent, enterprise requirements, research, technology, institutions and opportunity through one integrated ecosystem. It brings together learning and enterprise execution so that capability does not remain theoretical and demand does not remain disconnected from talent."
      >
        <ArrowBtn to="#ecosystem" variant="gold" testId="about-cta-ecosystem">Explore the DITEC Ecosystem</ArrowBtn>
      </PageHero>

      {/* Why DITEC Exists */}
      <section className="bg-paper py-20 sm:py-28">
        <div className="wrap">
          <SectionTag>01 — Why DITEC Exists</SectionTag>
          <Reveal delay={0.05}><h2 className="text-h2 mt-5 max-w-3xl font-semibold">Two Problems. <span className="font-serif italic font-normal text-electric">One Connected Opportunity.</span></h2></Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {ctList('about.why', [
              { t: 'For Young People', d1: 'A qualification alone does not always demonstrate whether someone can actually build, execute or generate value.', d2: "DITEC's learning model is designed to close that gap through practical work, digital assets, projects, measurable performance and commercial exposure." },
              { t: 'For Enterprises', d1: 'Companies need dependable technical and digital capability, but finding, evaluating and deploying that capability can be expensive and time-consuming.', d2: 'DITEC connects enterprise requirements with a developing pool of performance-backed talent and institutional capabilities.' },
              { t: 'For Institutions & Government', d1: 'There is also a need for delivery mechanisms that can turn programmes and policy objectives into measurable outcomes.', d2: 'DITEC is designed to operate at this intersection.' },
            ]).map((c, i) => (
              <Reveal key={c.t} delay={i * 0.08} className="rounded-3xl border border-navy/10 bg-white p-8 sm:p-9">
                <h3 className="text-h3 font-semibold text-navy">{c.t}</h3>
                <p className="mt-4 text-sm leading-relaxed text-navy/70">{c.d1}</p>
                <p className="mt-3 text-sm leading-relaxed text-navy/70">{c.d2}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15} className="mt-12">
            <div className="rounded-3xl border border-navy/10 bg-white p-8 sm:p-10">
              <span className="overline !text-[10px] text-electric">What DITEC Connects</span>
              <div className="mt-5">
                <ChainFlow steps={['Talent', 'Capability', 'Projects', 'Enterprise Demand', 'Data', 'Capital', 'More Opportunity']} testId="about-chain" />
              </div>
              <p className="mt-5 text-sm text-navy/60">The ecosystem is designed so that activity on one side strengthens the others.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Seven Ecosystem Layers */}
      <section id="ecosystem" className="bg-grid-dark bg-navy py-20 text-white sm:py-28">
        <div className="wrap">
          <SectionTag dark>02 — The Seven Ecosystem Layers</SectionTag>
          <Reveal delay={0.05}><h2 className="text-h2 mt-5 max-w-3xl font-semibold">One Institution. <span className="font-serif italic font-normal text-gold">Seven Interlocking Layers.</span></h2></Reveal>
          <Reveal delay={0.1} className="mt-14"><LayersExplorer /></Reveal>
        </div>
      </section>

      {/* Triad Database */}
      <section className="bg-paper py-20 sm:py-28">
        <div className="wrap">
          <SectionTag>03 — The Triad Database</SectionTag>
          <Reveal delay={0.05}><h2 className="text-h2 mt-5 max-w-3xl font-semibold">Three Forces. <span className="font-serif italic font-normal text-electric">One Intelligence Layer.</span></h2></Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {ctList('about.triad', TRIAD).map((t, i) => (
              <Reveal key={t.title} delay={i * 0.08} className="relative rounded-3xl bg-navy p-8 text-white sm:p-9">
                <span className="font-mono text-xs text-gold/70">NODE {String.fromCharCode(65 + i)}</span>
                <h3 className="text-h3 mt-4 font-semibold">{t.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-white/65">{t.desc}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <div className="mt-10 rounded-3xl border border-navy/10 bg-white p-8 sm:p-10">
              <h3 className="text-h3 font-semibold">Why It Matters</h3>
              <p className="mt-3 max-w-3xl text-sm leading-relaxed text-navy/70 sm:text-base">
                Instead of functioning as a static directory, the database is designed to become increasingly useful as projects, talent and enterprise activity generate new information.
              </p>
              <div className="mt-6"><ArrowBtn to="/enterprises" variant="navy" testId="about-cta-enterprise-ecosystem">Explore the Enterprise Ecosystem</ArrowBtn></div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* DITEC Journey */}
      <section className="bg-white py-20 sm:py-28">
        <div className="wrap">
          <SectionTag>04 — The DITEC Journey</SectionTag>
          <Reveal delay={0.05}><h2 className="text-h2 mt-5 font-semibold">From Learning to <span className="font-serif italic font-normal text-electric">Economic Participation</span></h2></Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {ctList('about.journey', JOURNEY).map((j, i) => (
              <Reveal key={j.step} delay={i * 0.05} className="flex items-start gap-5 rounded-3xl border border-navy/10 bg-paper p-7">
                <span className="font-mono text-2xl font-semibold text-electric/25">{String(i + 1).padStart(2, '0')}</span>
                <div>
                  <h3 className="text-lg font-semibold tracking-tight">{j.step}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-navy/65">{j.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Institutional Architecture */}
      <section className="bg-paper py-20 sm:py-28">
        <div className="wrap grid items-center gap-12 lg:grid-cols-2">
          <div>
            <SectionTag>05 — Institutional Architecture</SectionTag>
            <Reveal delay={0.05}><h2 className="text-h2 mt-5 font-semibold">Designed to <span className="font-serif italic font-normal text-electric">Scale</span></h2></Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-base leading-relaxed text-navy/75 sm:text-lg">DITEC's operating architecture combines:</p>
              <ul className="mt-5 space-y-3">
                {['Central institutional leadership', 'Academic and practitioner-led delivery', 'Enterprise and commercial engagement'].map((l) => (
                  <li key={l} className="flex items-center gap-3 text-sm font-medium text-navy sm:text-base"><span className="h-1.5 w-1.5 rounded-full bg-electric" />{l}</li>
                ))}
              </ul>
              <p className="mt-6 text-sm leading-relaxed text-navy/65 sm:text-base">
                This allows the curriculum to remain connected to active industry practice while the wider organisation develops institutional partnerships, research and enterprise relationships.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.12}>
            <div className="overflow-hidden rounded-3xl border border-navy/10 shadow-2xl shadow-navy/15">
              <img src={IMAGES.architecture} alt="Modern institutional architecture" className="aspect-[4/3] w-full object-cover" loading="lazy" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Road Ahead */}
      <section className="bg-grid-dark bg-navy py-20 text-white sm:py-28">
        <div className="wrap">
          <SectionTag dark>06 — The Road Ahead</SectionTag>
          <Reveal delay={0.05}><h2 className="text-h2 mt-5 max-w-3xl font-semibold">From Local Capability to <span className="font-serif italic font-normal text-gold">Global Opportunity</span></h2></Reveal>
          <Reveal delay={0.1} className="mt-12">
            <div className="flex flex-wrap items-center gap-2">
              {ROADMAP.map((r, i) => (
                <span key={r} className="flex items-center gap-2">
                  <span className={`rounded-full border px-5 py-3 text-sm font-semibold tracking-tight transition-colors duration-300 ${i === 0 ? 'border-gold bg-gold text-navy' : 'border-white/20 text-white/80 hover:border-gold/60 hover:text-gold'}`} data-testid={`roadmap-step-${i}`}>{r}</span>
                  {i < ROADMAP.length - 1 && <span className="font-serif italic text-gold/60">→</span>}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 max-w-3xl text-base leading-relaxed text-white/70 sm:text-lg">
              The broader direction is to expand from underserved districts and institutional partnerships toward multi-city operations, government relationships and eventually international service delivery and global talent pipelines.
            </p>
          </Reveal>
          <Reveal delay={0.2} className="mt-10"><ArrowBtn onClick={() => open('Strategic Partnership')} variant="gold" testId="about-cta-partner">Partner With DITEC</ArrowBtn></Reveal>
        </div>
      </section>
    </div>
  );
}
